import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ShoppingBag } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

export default function Shop() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [previewIndex, setPreviewIndex] = useState({});
  const [buying, setBuying] = useState(null); // product being bought
  const [buyer, setBuyer] = useState({ buyer_name: "", email: "", whatsapp_number: "", address: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const setPreview = (pid, i) => setPreviewIndex((s) => ({ ...s, [pid]: i }));

  const openBuy = (product) => {
    setBuyer({ buyer_name: "", email: "", whatsapp_number: "", address: "", quantity: 1 });
    setBuying(product);
  };

  const startCheckout = async () => {
    if (!buyer.buyer_name.trim() || !buyer.email.trim() || !buyer.whatsapp_number.trim()) {
      toast.error("Please fill in name, email and WhatsApp number");
      return;
    }
    setSubmitting(true);
    try {
      const orderRes = await api.post("/shop/orders", { ...buyer, product_id: buying.id });
      const orderId = orderRes.data.id;
      const useRazorpay = !!(settings?.razorpay_enabled && settings?.razorpay_key_id);
      if (!useRazorpay) {
        toast.success("Order received! Our team will reach out shortly to arrange payment.");
        setBuying(null);
        return;
      }
      const sdk = await loadRazorpay();
      if (!sdk) throw new Error("Failed to load Razorpay");
      const rpRes = await api.post(`/shop/orders/${orderId}/razorpay-order`);
      const { order_id, amount, currency, key_id, buyer: prefill, product_name } = rpRes.data;

      const rzp = new window.Razorpay({
        key: key_id,
        order_id,
        amount,
        currency,
        name: "Furshala",
        description: product_name,
        image: "https://customer-assets.emergentagent.com/job_b900a691-e276-4f55-b283-229aa9881e7a/artifacts/kduegc3q_furshala_logo_-removebg-preview.png",
        prefill: { name: prefill.name, email: prefill.email, contact: prefill.contact },
        notes: { shop_order_id: orderId },
        theme: { color: "#631F22" },
        handler: async (response) => {
          try {
            await api.post(`/shop/orders/${orderId}/razorpay-verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment received! We&apos;ll be in touch about shipping.");
            setBuying(null);
          } catch (e) {
            toast.error("Payment verification failed. Contact us with your reference.");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled — your order is held.") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="shop-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-10">
        <div className="eyebrow mb-3">Naturally crafted</div>
        <h1 className="font-serif text-4xl sm:text-6xl text-brand-soft max-w-3xl leading-[1.05]">
          {settings?.shop_section_label || "New Arrivals"} <span className="italic text-brand-maroon">for the love of paws.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-brand-soft/70 max-w-2xl">Cotton, hemp, plant dyes. Stitched in small batches by hands that care.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-24">
        {products.length === 0 ? (
          <div className="text-brand-soft/60 italic">Our shop is coming soon — peek back in a bit.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => {
              const imgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : []));
              const idx = previewIndex[p.id] || 0;
              const current = imgs[idx] || "";
              return (
                <div key={p.id} className="group bg-white rounded-2xl overflow-hidden border border-brand-maroon/10 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`product-card-${p.id}`}>
                  <div className="aspect-square overflow-hidden bg-brand-ivory relative">
                    {current && <img src={fileUrl(current)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    {imgs.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {imgs.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setPreview(p.id, i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-brand-maroon w-5" : "bg-white/80 hover:bg-white"}`}
                            aria-label={`Image ${i + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-xl text-brand-soft">{p.name}</h3>
                      <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${p.tag === "upcoming" ? "bg-brand-mustard/25 text-brand-maroon" : "bg-brand-olive/15 text-brand-olive"}`}>{p.tag === "upcoming" ? "Upcoming" : "New"}</span>
                    </div>
                    <p className="text-sm text-brand-soft/70 leading-relaxed">{p.description}</p>
                    <div className="flex items-center justify-between pt-2">
                      <div className="font-serif text-2xl text-brand-maroon">₹{p.price}</div>
                      {p.available ? (
                        <button onClick={() => openBuy(p)} className="text-xs font-medium px-4 py-2 rounded-full bg-brand-maroon text-brand-cream hover:bg-brand-maroon-dark transition" data-testid={`buy-${p.id}`}>Buy</button>
                      ) : (
                        <span className="text-xs uppercase tracking-widest text-brand-soft/50">Sold out</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Buy modal */}
      {buying && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setBuying(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-7 sm:p-10" onClick={(e) => e.stopPropagation()} data-testid="buy-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-brand-soft inline-flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-brand-maroon" /> Order details
              </h2>
              <button onClick={() => setBuying(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-brand-ivory rounded-2xl p-4 mb-5 flex gap-4 items-center">
              {buying.image && <img src={fileUrl(buying.image)} alt={buying.name} className="w-16 h-16 rounded-lg object-cover" />}
              <div className="flex-1">
                <div className="font-serif text-lg text-brand-soft">{buying.name}</div>
                <div className="text-sm text-brand-maroon font-medium">₹{buying.price} × {buyer.quantity} = ₹{buying.price * buyer.quantity}</div>
              </div>
            </div>
            <div className="space-y-3">
              <Input label="Your name *" value={buyer.buyer_name} onChange={(v) => setBuyer({ ...buyer, buyer_name: v })} testId="buyer-name" />
              <Input label="Email *" type="email" value={buyer.email} onChange={(v) => setBuyer({ ...buyer, email: v })} testId="buyer-email" />
              <Input label="WhatsApp number *" value={buyer.whatsapp_number} onChange={(v) => setBuyer({ ...buyer, whatsapp_number: v })} testId="buyer-whatsapp" />
              <label className="block">
                <div className="text-sm font-medium text-brand-soft mb-1.5">Quantity</div>
                <input type="number" min="1" max="20" value={buyer.quantity} onChange={(e) => setBuyer({ ...buyer, quantity: Math.max(1, Number(e.target.value) || 1) })} className="w-24 bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
              </label>
              <label className="block">
                <div className="text-sm font-medium text-brand-soft mb-1.5">Shipping address <span className="text-xs text-brand-soft/50 font-normal">(optional — we&apos;ll confirm by WhatsApp)</span></div>
                <textarea rows={2} value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
              </label>
            </div>
            <button onClick={startCheckout} disabled={submitting} className="btn-primary-furshala w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="buyer-checkout">
              {submitting ? "Processing…" : `Pay ₹${buying.price * buyer.quantity}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", testId }) => (
  <label className="block">
    <div className="text-sm font-medium text-brand-soft mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid={testId} />
  </label>
);
